import React, { useCallback, useMemo, useState } from "react";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";
import MetricCard from "./MetricCard";
import Button from "../../../components/shared/Button/Button";
import Select from "../../../components/shared/Select/Select";
import { HorizontalScrollWrapper } from "../../../components/shared/HorizontalScrollWrapper/HorizontalScrollWrapper";
import { EMPTY_METRIC } from "../../swing_library/utils/metrics";
import { useGetOutliers } from "../hooks/useGetOutliers";
import { useOutliersFilters } from "../hooks/useOutliersFilters";

export default function Top5Outliers({ collection1, collection2 }) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selectedKeyPosition, setSelectedKeyPosition] = useState(null);
  const [selectedMovementType, setSelectedMovementType] = useState(null);

  // Outlier filter options (no auth)
  const { data: filtersData } = useOutliersFilters();

  const keyPositionOptions = useMemo(
    () =>
      filtersData?.keyPositions?.map((p) => ({
        text: p.label,
        value: p.value,
      })) ?? [],
    [filtersData?.keyPositions],
  );
  const movementTypeOptions = useMemo(
    () =>
      filtersData?.movementTypes?.map((m) => ({
        text: m.label,
        value: m.value,
      })) ?? [],
    [filtersData?.movementTypes],
  );

  const handleKeyPositionChange = useCallback((val) => {
    setSelectedKeyPosition((prev) => (val === prev ? null : (val ?? null)));
  }, []);
  const handleMovementTypeChange = useCallback((val) => {
    setSelectedMovementType((prev) => (val === prev ? null : (val ?? null)));
  }, []);

  // Filter for getOutliers API (keyPosition takes precedence if both selected)
  const outliersFilter = useMemo(() => {
    if (selectedKeyPosition)
      return { type: "keyPosition", value: selectedKeyPosition };
    if (selectedMovementType)
      return { type: "movementType", value: selectedMovementType };
    return null;
  }, [selectedKeyPosition, selectedMovementType]);

  const primaryVideoIds = collection1?.videoIds ?? [];
  const secondaryVideoIds = collection2?.videoIds ?? [];

  const {
    data: outliersResponse,
    isLoading: isLoadingOutliers,
    isFetching: isFetchingOutliers,
  } = useGetOutliers(primaryVideoIds, secondaryVideoIds, outliersFilter);

  // Map getOutliers API result to card data. API returns { result: [...] } with
  // primaryCollection/secondaryCollection: { average, deviation, zScore }.
  const cardData = useMemo(() => {
    const list = outliersResponse?.result ?? [];
    if (!Array.isArray(list)) return [];
    return list.slice(0, 5).map((item) => {
      const indicatorId = item.indicatorId ?? item.id;
      const title = item.indicatorName ?? indicatorId;
      const unit = item.indicatorUnit ?? null;
      const primary = item.primaryCollection;
      const secondary = item.secondaryCollection;
      const toMetric = (coll, u) =>
        !coll
          ? EMPTY_METRIC
          : {
              ...EMPTY_METRIC,
              avg: coll.average,
              stdDev: coll.deviation ?? null,
              unit: u ?? null,
              zScore: coll.zScore ?? null,
              zScoreColor: null,
            };
      return {
        indicatorId,
        metadata: { title, unit },
        metric1: toMetric(primary, unit),
        metric2: toMetric(secondary, unit),
      };
    });
  }, [outliersResponse]);

  const isLoading = isLoadingOutliers || isFetchingOutliers;

  // Don't render if no collections are provided
  if (!collection1 && !collection2) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-4">
        <h2 className="text-2xl font-semibold text-black">Top 5 Outliers</h2>

        <Button
          className="flex items-center gap-1"
          color="gray100"
          onClick={() => setIsCollapsed((prev) => !prev)}
          size="sm"
        >
          {isCollapsed ? "Expand" : "Collapse"}
          {isCollapsed ? (
            <HiChevronDown className="size-5" />
          ) : (
            <HiChevronUp className="size-5" />
          )}
        </Button>
      </div>

      {!isCollapsed && (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <Select
              btnInitialColor="gray100"
              btnSelectedColor="white/primary700"
              disabled={!!selectedMovementType}
              header="Key Position"
              options={keyPositionOptions}
              placeholder="Key Position"
              value={selectedKeyPosition}
              onChange={handleKeyPositionChange}
              menuClassName="w-[350px] max-h-[500px] overflow-y-auto"
              withCheckbox
            />
            <Select
              btnInitialColor="gray100"
              btnSelectedColor="white/primary700"
              disabled={!!selectedKeyPosition}
              header="Movement Type"
              options={movementTypeOptions}
              placeholder="Movement Type"
              menuClassName="w-[200px] max-h-[500px] overflow-y-auto"
              value={selectedMovementType}
              onChange={handleMovementTypeChange}
              withCheckbox
            />
          </div>

          <HorizontalScrollWrapper>
            {cardData.length > 0 ? (
              cardData.map(({ indicatorId, metadata, metric1, metric2 }) => (
                <MetricCard
                  className="w-1/4 min-w-[25%]"
                  collection1={collection1}
                  collection2={collection2}
                  isLoading={isLoading}
                  key={indicatorId}
                  metric1={metric1}
                  metric2={metric2}
                  title={metadata.title}
                  unit={metadata.unit || metric1.unit || metric2.unit}
                />
              ))
            ) : (
              <div className="text-gray500 py-4 text-sm">
                {isLoading ? "Loading outliers..." : "No outliers found."}
              </div>
            )}
          </HorizontalScrollWrapper>
        </>
      )}
    </div>
  );
}
